import React, { useEffect, useState } from 'react';
import { Share2, ExternalLink, QrCode, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';

const KIND_LABEL = { app: 'App', p: 'Lugar', e: 'Evento', a: 'Artículo', t: 'Ruta', w: 'Webcam', b: 'Playa/spot' };
const CHANNEL_LABEL = { native: 'Hoja nativa', whatsapp: 'WhatsApp', telegram: 'Telegram', x: 'X', facebook: 'Facebook', copy: 'Copiar enlace', qr: 'QR', email: 'Correo' };

export const QrDownloadButton = ({ kind, itemId, name, className = '' }) => (
  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/share/qr/${kind}${itemId ? `/${itemId}` : ''}.png`} download={`qr-${(name || kind).toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`}
    className={`p-2 text-gray-500 hover:text-[#38B2AC] hover:bg-gray-50 rounded-lg inline-flex ${className}`} title="Descargar QR" data-testid={`qr-btn-${itemId || kind}`}>
    <QrCode size={18} />
  </a>
);

const Ranking = ({ title, items, testId }) => (
  <div className="bg-white border border-gray-200" data-testid={testId}>
    <p className="px-4 py-3 font-bold text-[#1D1D1F] border-b border-gray-100">{title}</p>
    {items.length === 0 && <p className="px-4 py-4 text-sm text-gray-400">Sin datos todavía</p>}
    {items.map((r, i) => (
      <div key={`${r.kind}-${r.item_id}-${i}`} className="flex items-center gap-3 px-4 py-2 border-b border-gray-50 last:border-0">
        <span className="w-5 text-xs text-gray-400">{i + 1}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1D1D1F] truncate">{r.name}</p>
          <p className="text-[11px] text-gray-400">{KIND_LABEL[r.kind] || r.kind}</p>
        </div>
        <span className="text-sm font-bold text-[#1D1D1F]">{r.count}</span>
        {r.kind !== 'app' && r.item_id && <QrDownloadButton kind={r.kind} itemId={r.item_id} name={r.name} />}
      </div>
    ))}
  </div>
);

const AdminShare = () => {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('');
  const [days, setDays] = useState('30');

  useEffect(() => {
    setData(null);
    api.adminShareStats({ kind: kind || undefined, days: days === 'all' ? undefined : Number(days) }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [kind, days]);

  const max = Math.max(1, ...(data?.qr_daily?.map((d) => d.count) || [1]));

  return (
    <div className="space-y-5" data-testid="admin-share">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-lg text-[#1D1D1F] flex items-center gap-2"><Share2 size={18} className="text-[#38B2AC]" /> Compartidos</h2>
          <p className="text-sm text-gray-500">Qué se comparte, qué se abre y cuántos entran escaneando los QR.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 border border-gray-200 px-2 text-sm bg-white" data-testid="share-filter-kind">
            <option value="">Todo</option>{Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(e.target.value)} className="h-9 border border-gray-200 px-2 text-sm bg-white" data-testid="share-filter-days">
            <option value="7">7 días</option><option value="30">30 días</option><option value="all">Todo</option>
          </select>
          <Button asChild variant="outline" className="h-9"><a href={`${process.env.REACT_APP_BACKEND_URL}/api/share/qr/app.png`} download="qr-calma-guide-tenerife.png" data-testid="share-app-qr"><QrCode size={16} className="mr-1.5" /> QR de la app</a></Button>
          <Button asChild variant="outline" className="h-9"><a href="https://guide.calmastays.com/api/s/app?l=es" target="_blank" rel="noopener noreferrer" data-testid="share-app-link"><ExternalLink size={16} className="mr-1.5" /> Enlace</a></Button>
        </div>
      </div>

      {data === null && <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#38B2AC]" /></div>}
      {data === false && <p className="text-sm text-gray-500">No se pudieron cargar las estadísticas.</p>}
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3" data-testid="share-totals">
            {[['Compartidos', data.totals.shares, 'share-total-shares'], ['Aperturas', data.totals.opens, 'share-total-opens'], ['Entradas por QR', data.totals.qr_entries, 'share-total-qr']].map(([l, v, id]) => (
              <div key={l} className="bg-white border border-gray-200 p-4">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">{l}</p>
                <p className="text-3xl font-extrabold text-[#1D1D1F]" data-testid={id}>{v}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border border-gray-200 p-4" data-testid="share-qr-chart">
            <p className="font-bold text-[#1D1D1F] mb-3">Entradas por QR por día</p>
            {data.qr_daily.length === 0 ? <p className="text-sm text-gray-400">Sin escaneos en el periodo</p> : (
              <div className="flex items-end gap-1 h-28">
                {data.qr_daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${d.date}: ${d.count}`}>
                    <span className="text-[10px] text-gray-500">{d.count}</span>
                    <div className="w-full bg-[#38B2AC]" style={{ height: `${Math.max(4, (d.count / max) * 90)}px` }} />
                    <span className="text-[9px] text-gray-400">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <Ranking title="Más compartido" items={data.rankings.share} testId="share-rank-share" />
            <Ranking title="Más abierto" items={data.rankings.open} testId="share-rank-open" />
            <Ranking title="Más escaneado (QR)" items={data.rankings.qr} testId="share-rank-qr" />
          </div>

          {Object.keys(data.channels).length > 0 && (
            <div className="bg-white border border-gray-200 p-4 flex flex-wrap gap-2" data-testid="share-channels">
              {Object.entries(data.channels).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                <span key={c} className="px-3 py-1 bg-[#FDFBF7] border border-gray-100 text-xs"><b>{n}</b> {CHANNEL_LABEL[c] || c}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminShare;
